import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import Header from 'components/ui/Header';
import ProgressIndicator from './components/ProgressIndicator';
import StatusDisplay from './components/StatusDisplay';
import PaymentMethodForm from './components/PaymentMethodForm';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const stripePromise = loadStripe(import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY);

const PaymentVerificationPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState('pending');
  const [clientSecret, setClientSecret] = useState(null);
  const [setupIntentId, setSetupIntentId] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [stripeLoadFailed, setStripeLoadFailed] = useState(false);
  const [showSkipBanner, setShowSkipBanner] = useState(true);

  useEffect(() => {
    const checkStripe = async () => {
      const stripe = await stripePromise;
      if (!stripe) {
        setStripeLoadFailed(true);
      }
    };
    checkStripe();
  }, []);

  // Fetch platform_config for skip banner visibility
  useEffect(() => {
    const fetchBannerConfig = async () => {
      try {
        const { data, error } = await supabase?.from('platform_config')?.select('value')?.eq('key', 'show_payment_skip_banner')?.single();
        if (error || !data) {
          setShowSkipBanner(true);
        } else {
          setShowSkipBanner(data?.value === true || data?.value === 'true');
        }
      } catch {
        setShowSkipBanner(true);
      }
    };
    fetchBannerConfig();
  }, []);

  // Initialize SetupIntent by calling the Supabase Edge Function with the user's auth token
  useEffect(() => {
    const initializeSetupIntent = async () => {
      if (!user) {
        setErrorMessage('User not authenticated');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();

        if (sessionError || !session) {
          throw new Error('User not authenticated');
        }

        const { data, error } = await supabase?.functions?.invoke('create-setup-intent', {
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
          },
        });

        if (error) {
          throw new Error(error?.message || 'Failed to create setup intent');
        }

        setClientSecret(data?.clientSecret);
        setSetupIntentId(data?.setupIntentId);
      } catch (err) {
        console.error('Error creating SetupIntent:', err);
        setErrorMessage(err?.message || 'Failed to initialize payment verification');
        setVerificationStatus('failed');
      } finally {
        setIsLoading(false);
      }
    };

    initializeSetupIntent();
  }, [user]);

  // Handle successful payment method verification
  const handleVerificationSuccess = async (confirmedSetupIntentId) => {
    setIsLoading(true);
    setVerificationStatus('pending');

    try {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // Bypass Stripe edge functions — directly update user row
      const { error } = await supabase
        ?.from('users')
        ?.update({ payment_authorized: true, registration_step: 3 })
        ?.eq('id', user?.id);

      if (error) {
        throw new Error(error?.message || 'Failed to update payment status');
      }

      setVerificationStatus('verified');
      navigate('/professional-dashboard');
    } catch (err) {
      console.error('Error updating payment status:', err);
      setErrorMessage(err?.message || 'Verification failed');
      setVerificationStatus('failed');
      setIsLoading(false);
    }
  };

  // Handle verification failure
  const handleVerificationError = (error) => {
    setErrorMessage(error?.message || 'Verification failed');
    setVerificationStatus('failed');
    setIsLoading(false);
  };

  // Navigate to professional dashboard
  const handleContinueToDashboard = () => {
    navigate('/professional-dashboard');
  };

  // Retry verification
  const handleRetryVerification = () => {
    window.location?.reload();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-3xl mx-auto px-4 pt-20 pb-8 sm:px-6 lg:px-8">
        <ProgressIndicator currentStep={3} />
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sm:p-8 mt-8">
          {/* Informational Message */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-blue-800 text-center">
              ℹ️ Serve solo per verificare il metodo di pagamento. Nessun addebito immediato.
            </p>
          </div>

          {/* Skip Banner */}
          {showSkipBanner && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-900 font-semibold text-center mb-1">
                💳 La carta è facoltativa
              </p>
              <p className="text-sm text-blue-800 text-center mb-3">
                Hai già completato la registrazione con i tuoi dati reali: profilo, professione e documenti. Il nostro sistema ti riconosce già come professionista, non come account automatico. La verifica della carta serve solo a scoraggiare i bot — nel tuo caso puoi saltarla con tranquillità.
              </p>
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={() => handleVerificationSuccess(null)}
                  className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-5 py-2 rounded-lg transition-colors"
                >
                  Salta e continua senza carta →
                </button>
              </div>
            </div>
          )}

          {/* Error Message */}
          {errorMessage && verificationStatus === 'failed' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-red-800 text-center">
                ❌ {errorMessage}
              </p>
            </div>
          )}

          {/* Status Display or Payment Form */}
          {stripeLoadFailed ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-red-800 text-center">
                ❌ Errore di configurazione del pagamento. Riprova più tardi o contatta il supporto.
              </p>
            </div>
          ) : verificationStatus === 'pending' && !isLoading && clientSecret ? (
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <PaymentMethodForm
                clientSecret={clientSecret}
                setupIntentId={setupIntentId}
                onSuccess={handleVerificationSuccess}
                onError={handleVerificationError}
                showSkipBanner={showSkipBanner}
              />
            </Elements>
          ) : (
            <StatusDisplay 
              isLoading={isLoading} 
              verificationStatus={verificationStatus} 
            />
          )}

          {/* Navigation Buttons */}
          {!isLoading && verificationStatus === 'verified' && (
            <div className="mt-8 flex justify-center">
              <button
                onClick={handleContinueToDashboard}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-medium transition-colors"
              >
                Continua alla Dashboard
              </button>
            </div>
          )}
          
          {!isLoading && verificationStatus === 'failed' && (
            <div className="mt-8 flex justify-center">
              <button
                onClick={handleRetryVerification}
                className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-lg font-medium transition-colors"
              >
                Riprova Verifica
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentVerificationPage;